export type ClinicProfileData = {
  tenantKey: string;
  displayName: string;
  addressText: string;
  hoursText: string;
  parkingText: string;
  renewalsPhone: string;
  supportPhone: string;
  noMedicalAdviceNotice: string;
};

export type TenantBranding = {
  logoPath?: string;
  supportTagline?: string;
};

export type TenantRegistryEntry = {
  tenantKey: string;
  whiteLabel: boolean;
  clinicProfile: ClinicProfileData;
  brand: TenantBranding;
};

export type WhiteLabelTenantConfig = {
  tenantKey: string;
  clinicName: string;
  locationLines: string[];
  hoursLines: string[];
  parkingText: string;
  renewalsPhone: string;
  supportPhone: string;
  noMedicalAdviceNotice: string;
  pricingFacts?: string[];
  bookingFacts?: string[];
  renewalFacts?: string[];
  contactFacts?: string[];
  callbackFacts?: string[];
  handoffFacts?: string[];
  logoPath?: string;
  supportTagline?: string;
};

export const DEFAULT_TENANT_KEY = "vetcan.astormscoming.com";
export const MARIJUANA_EXPRESS_TENANT_KEY =
  "marijuanaexpressmd.astormscoming.com";

const NO_MEDICAL_ADVICE_NOTICE =
  "This chat can help with operations and scheduling only and does not provide medical advice.";

export const TENANT_REGISTRY: Record<string, TenantRegistryEntry> = {
  [DEFAULT_TENANT_KEY]: {
    tenantKey: DEFAULT_TENANT_KEY,
    whiteLabel: false,
    clinicProfile: {
      tenantKey: DEFAULT_TENANT_KEY,
      displayName: "VetCan Clinic",
      addressText: "123 Main St, Springfield, ST 12345",
      hoursText:
        "Mon 8:00 AM - 5:00 PM, Tue 8:00 AM - 5:00 PM, Wed 8:00 AM - 5:00 PM, Thu 8:00 AM - 5:00 PM, Fri 8:00 AM - 4:00 PM, Sat 9:00 AM - 1:00 PM, Sun Closed",
      parkingText:
        "Free patient parking is available in front of the clinic and in the side lot.",
      renewalsPhone: "(555) 010-2020",
      supportPhone: "(555) 010-2020",
      noMedicalAdviceNotice: NO_MEDICAL_ADVICE_NOTICE,
    },
    brand: {
      logoPath: "/logo.svg",
      supportTagline: "Clinic scheduling and support",
    },
  },
  [MARIJUANA_EXPRESS_TENANT_KEY]: {
    tenantKey: MARIJUANA_EXPRESS_TENANT_KEY,
    whiteLabel: true,
    clinicProfile: {
      tenantKey: MARIJUANA_EXPRESS_TENANT_KEY,
      displayName: "Marijuana Express MD",
      addressText: "909 FL-436, Casselberry, FL 32707",
      hoursText: "Mon - Fri 10 AM - 6 PM; Select Saturdays 10 AM - 4 PM",
      parkingText: "On-site parking lot",
      renewalsPhone: "1-800-803-8525",
      supportPhone: "(855) 600-8710",
      noMedicalAdviceNotice: NO_MEDICAL_ADVICE_NOTICE,
    },
    brand: {
      logoPath: "/brand/mexpress-logo.png",
      supportTagline: "Clinic scheduling and support",
    },
  },
};

function splitDisplayLines(value: string) {
  return value
    .split(/\n+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function toWhiteLabelTenantConfig(entry: TenantRegistryEntry): WhiteLabelTenantConfig {
  const base = {
    tenantKey: entry.tenantKey,
    clinicName: entry.clinicProfile.displayName,
    locationLines: splitDisplayLines(entry.clinicProfile.addressText),
    hoursLines: splitDisplayLines(entry.clinicProfile.hoursText),
    parkingText: entry.clinicProfile.parkingText,
    renewalsPhone: entry.clinicProfile.renewalsPhone,
    supportPhone: entry.clinicProfile.supportPhone,
    noMedicalAdviceNotice: entry.clinicProfile.noMedicalAdviceNotice,
    logoPath: entry.brand.logoPath,
    supportTagline: entry.brand.supportTagline,
  };

  if (entry.tenantKey === MARIJUANA_EXPRESS_TENANT_KEY) {
    return {
      ...base,
      pricingFacts: [
        "New patient paid in full: $199 + 4% transaction fee ($206.96 displayed total).",
        "New patient deposit path: $50 deposit + 4% transaction fee today. The displayed total for that offer is $269.30; remaining payment handling stays payment-safe and staff-confirmed.",
        "State fee: $75 is paid separately after doctor qualification.",
        "Renewal pricing depends on the approved lane. Standard renewals are $139 + 4% transaction fee ($144.56 total), veteran renewals are $119 + 4% ($123.76 total), double veteran renewal tele is $99 + 4% ($102.96 total), and industry renewals are $75 + 4% ($78 total). Payment-safe next steps and any funding-style, exception, or route-specific question stay staff-confirmed.",
      ],
      bookingFacts: [
        "Bring photo ID when possible.",
        "Identification or residency-edge-case questions stay staff-confirmed.",
        "Watch for emailed forms and complete them before the appointment when possible.",
        "Medical questions and eligibility decisions stay with clinic staff.",
      ],
      renewalFacts: [
        "Renewals are handled by telephone with no video required.",
        "Renewal appointments are paid in full before the appointment is confirmed. Standard renewals are $139 + 4% ($144.56 total), veteran renewals are $119 + 4% ($123.76 total), double veteran renewal tele is $99 + 4% ($102.96 total), and industry renewals are $75 + 4% ($78 total). Payment-safe next steps and any funding-style, exception, or route-specific question stay staff-confirmed.",
        "Keep your phone nearby 30 minutes before and after the appointment in case timing shifts.",
        "Save the highlighted doctor callback number from the confirmation email so you do not miss the call.",
      ],
      contactFacts: [
        "Call or text 24/7: (855) 600-8710.",
        "Renew by telephone (no video): 1-800-803-8525.",
        "If availability is unclear, staff follow-up is queued instead of guessing.",
      ],
      callbackFacts: [
        "If timing is unclear, staff follow-up is queued instead of guessing a slot.",
        "Share the best callback number and the best time window so staff can return the call cleanly.",
        "If you are not ready to finish today, staff can arrange the next callback step.",
      ],
      handoffFacts: [
        "Use staff handoff for booking questions, payment timing, secure-link follow-up, renewal timing, or anything that needs human confirmation.",
        "Transfer or prior-clinic questions stay staff-confirmed when the route is unclear.",
        "Chat stays on operational guidance only and does not give medical advice.",
        "When human follow-up is needed, the goal is a clean callback with the right context already captured.",
      ],
    };
  }

  return base;
}

export function normalizeTenantHostname(value: string) {
  let normalized = value.trim().toLowerCase();
  if (!normalized) return "";

  if (normalized.includes("://")) {
    try {
      normalized = new URL(normalized).host;
    } catch {
      // Keep original when parsing fails.
    }
  }

  normalized = normalized.split("/")[0] || normalized;
  normalized = normalized.split("?")[0] || normalized;
  normalized = normalized.split("#")[0] || normalized;

  if (normalized.includes(":")) {
    normalized = normalized.split(":")[0] || normalized;
  }

  normalized = normalized.replace(/\.$/, "");
  normalized = normalized.replace(/^www\./, "");

  return normalized;
}

export function getTenantRegistryEntry(tenantKey: string): TenantRegistryEntry {
  const normalized = normalizeTenantHostname(tenantKey);
  return TENANT_REGISTRY[normalized] || TENANT_REGISTRY[DEFAULT_TENANT_KEY];
}

export function getTenantConfig(hostname: string): WhiteLabelTenantConfig {
  return toWhiteLabelTenantConfig(getTenantRegistryEntry(hostname));
}

export function getClinicProfileFallback(tenantKey: string): ClinicProfileData {
  const normalized = normalizeTenantHostname(tenantKey);
  const knownTenant = TENANT_REGISTRY[normalized];

  if (knownTenant) {
    return { ...knownTenant.clinicProfile };
  }

  if (!normalized || normalized === DEFAULT_TENANT_KEY) {
    return { ...TENANT_REGISTRY[DEFAULT_TENANT_KEY].clinicProfile };
  }

  const firstLabel = normalized.split(".")[0] || "";
  const humanizedDisplayName = firstLabel
    .split(/[-_]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ");
  const genericLabel = humanizedDisplayName || "Clinic";
  const genericDetails = "Contact staff for details.";

  return {
    tenantKey: normalized,
    displayName: genericLabel,
    addressText: genericDetails,
    hoursText: genericDetails,
    parkingText: genericDetails,
    renewalsPhone: genericDetails,
    supportPhone: genericDetails,
    noMedicalAdviceNotice: NO_MEDICAL_ADVICE_NOTICE,
  };
}

export function getTenantBranding(tenantKey: string): TenantBranding {
  return { ...getTenantRegistryEntry(tenantKey).brand };
}

export function getWhiteLabelTenantConfig(
  hostname: string
): WhiteLabelTenantConfig | null {
  const normalized = normalizeTenantHostname(hostname);
  const entry = TENANT_REGISTRY[normalized];

  if (!entry || !entry.whiteLabel) {
    return null;
  }

  return toWhiteLabelTenantConfig(entry);
}
